
import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';

const TRANSITION_MS = 2000;
const AUTOPLAY_MS = 2000;
const CARD_WIDTH = 390; // fixed width of each card
const CARD_GAP = 18;    // gap between cards

const SportsBanner = () => {
  const history = useHistory();
  const containerRef = useRef(null);

  // --- Carousel state ---
  const [viewportCards, setViewportCards] = useState(
    typeof window !== 'undefined' && window.innerWidth <= 768 ? 1 : 3
  );
  const [currentSlide, setCurrentSlide] = useState(
    typeof window !== 'undefined' && window.innerWidth <= 768 ? 1 : 3 // start at first "real" index
  );

  // --- Data state ---
  const [isLoading, setIsLoading] = useState(true);
  const [mobileMatches, setMobileMatches] = useState([]);
  const [sporttab, setSportTab] = useState('all-sports');
  const [allSportsMatches, setAllSportsMatches] = useState([]);
  const [cricketMatches, setCricketMatches] = useState([]);
  const [soccerMatches, setSoccerMatches] = useState([]);
  const [tennisMatches, setTennisMatches] = useState([]);

  // Static tabs
  const sportsData = [
    { id: 1, name: 'In Play', tab: 'all-sports', image: '/assets/images/sports/sports_inplay.png' },
    { id: 2, name: 'Cricket', tab: 'cricket', image: '/assets/images/sports/sports_cricket.avif' },
    { id: 3, name: 'Soccer', tab: 'soccer', image: '/assets/images/sports/sports_soccer.avif' },
    { id: 4, name: 'Tennis', tab: 'tennis', image: '/assets/images/sports/sports_tennis.avif' },
    
  ];

  const totalSlides = sportsData.length;

  // Build extended data for smooth infinite loop depending on cards per view
  const extendedSportsData = [
    ...sportsData.slice(-viewportCards),
    ...sportsData,
    ...sportsData.slice(0, viewportCards),
    ...sportsData,

    ...sportsData.slice(1, viewportCards),
    ...sportsData,

  ];

  // --- Responsive: recalc cards per view on resize ---
  useEffect(() => {
    const onResize = () => {
      const next = window.innerWidth <= 768 ? 1 : 3;
      setViewportCards(next);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Keep currentSlide aligned when viewportCards changes
  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.style.transition = 'none';
    setCurrentSlide(viewportCards);
    // Re-enable transition on next frame
    requestAnimationFrame(() => {
      if (containerRef.current) {
        containerRef.current.style.transition = 'transform 0.5s ease';
      }
    });
  }, [viewportCards]);

  // --- Auto slide ---
  useEffect(() => {
    const slideInterval = setInterval(() => {
      setCurrentSlide((prev) => {
        const next = prev + 1;
        // Highest allowed leftmost index to still fill the view is (extendedLen - viewportCards)
        const maxLeftIndex = totalSlides + viewportCards; // since extendedLen = totalSlides + 2*viewportCards
        if (next > maxLeftIndex) {
          // After the current transition ends, jump back without animation
          setTimeout(() => {
            if (!containerRef.current) return;
            containerRef.current.style.transition = 'none';
            setCurrentSlide(viewportCards);
            // Re-enable transition on next frame
            requestAnimationFrame(() => {
              if (containerRef.current) {
                containerRef.current.style.transition = 'transform 0.5s ease';
              }
            });
          }, TRANSITION_MS);
          return next;
        }
        return next;
      });
    }, AUTOPLAY_MS);

    return () => clearInterval(slideInterval);
  }, [totalSlides, viewportCards]);

  // --- Data fetching (your original logic, with small safety tweaks) ---
  useEffect(() => {
    const fetchAllMatches = async () => {
      setIsLoading(true);
      try {
        const [inPlayResponse, allMatchesResponse] = await Promise.all([
          fetch('https://bkk07.com/sports/allinplay'),
          fetch('https://bkk07.com/sports/all-matches')
        ]);

        const inPlayData = await inPlayResponse.json();
        const allMatchesData = await allMatchesResponse.json();

        const inPlayCricketData = inPlayData.find(sport => sport.id === "4")?.markets || [];
        const allCricketData = allMatchesData.find(sport => sport.id === "4")?.markets || [];
        const allSportsData = allMatchesData?.map((s) => s?.markets) || [];
        const soccerData = allMatchesData.find(sport => sport.id === "1")?.markets || [];
        const tennisData = allMatchesData.find(sport => sport.id === "2")?.markets || [];

        const wholeSportsData = [
          ...(allSportsData[0] || []),
          ...(allSportsData[1] || []),
          ...(allSportsData[2] || []),
        ];

        const processMatches = (matches, inPlayMatches = []) => {
          const matchesMap = new Map();

          // In-Play first
          inPlayMatches.forEach(match => {
            matchesMap.set(match.marketId, {
              id: match.marketId,
              team1: match.runners?.[0]?.runnerName || '',
              team2: match.runners?.[1]?.runnerName || '',
              status: 'In-Play',
              isSuspended: match.stopBet,
              inplay: true,
              eventid: match?.version,
              marketStartTime: match.marketStartTime,
              odds: { one: { back: '--', lay: '--' }, x: { back: '--', lay: '--' }, two: { back: '--', lay: '--' } }
            });
          });

          // Upcoming / scheduled
          matches.forEach(match => {
            if (!matchesMap.has(match.marketId)) {
              const utcDate = new Date(match.marketStartTime);
              const istTime = new Intl.DateTimeFormat('en-IN', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
              }).format(utcDate);
              matchesMap.set(match.marketId, {
                id: match.marketId,
                team1: match.runners?.[0]?.runnerName || '',
                team2: match.runners?.[1]?.runnerName || '',
                status: `${istTime}`,
                isSuspended: match.stopBet,
                inplay: false,
                eventid: match?.version,
                marketStartTime: match.marketStartTime,
                odds: { one: { back: '--', lay: '--' }, x: { back: '--', lay: '--' }, two: { back: '--', lay: '--' } }
              });
            }
          });

          return Array.from(matchesMap.values());
        };

        const allSportsProcessed = processMatches(wholeSportsData, inPlayCricketData);
        const cricketProcessed = processMatches(allCricketData, inPlayCricketData);
        const soccerProcessed = processMatches(soccerData, []);
        const tennisProcessed = processMatches(tennisData, []);

        setAllSportsMatches(allSportsProcessed);
        setCricketMatches(cricketProcessed);
        setSoccerMatches(soccerProcessed);
        setTennisMatches(tennisProcessed);

        // initialize visible set
        if (sporttab === "all-sports") setMobileMatches(allSportsProcessed);
        else if (sporttab === "cricket") setMobileMatches(cricketProcessed);
        else if (sporttab === "soccer") setMobileMatches(soccerProcessed);
        else if (sporttab === "tennis") setMobileMatches(tennisProcessed);

        const fetchMatchOdds = async (matchId) => {
          try {
            const response = await fetch(`https://bkk07.com/sports/market-odds?market_id=${matchId}`);
            const data = await response.json();
            const marketOdds = data?.[0];
            if (!marketOdds || !marketOdds.runners) return null;

            const runners = marketOdds.runners;
            let formattedOdds = { one: { back: '--', lay: '--' }, x: { back: '--', lay: '--' }, two: { back: '--', lay: '--' } };

            if (runners.length === 2) {
              formattedOdds.one = {
                back: runners[0]?.ex?.availableToBack?.[0]?.price ?? '--',
                lay: runners[0]?.ex?.availableToLay?.[0]?.price ?? '--'
              };
              formattedOdds.two = {
                back: runners[1]?.ex?.availableToBack?.[0]?.price ?? '--',
                lay: runners[1]?.ex?.availableToLay?.[0]?.price ?? '--'
              };
            } else if (runners.length === 3) {
              formattedOdds.one = {
                back: runners[0]?.ex?.availableToBack?.[0]?.price ?? '--',
                lay: runners[0]?.ex?.availableToLay?.[0]?.price ?? '--'
              };
              formattedOdds.x = {
                back: runners[1]?.ex?.availableToBack?.[0]?.price ?? '--',
                lay: runners[1]?.ex?.availableToLay?.[0]?.price ?? '--'
              };
              formattedOdds.two = {
                back: runners[2]?.ex?.availableToBack?.[0]?.price ?? '--',
                lay: runners[2]?.ex?.availableToLay?.[0]?.price ?? '--'
              };
            }
            return formattedOdds;
          } catch (error) {
            console.error('Error fetching odds:', error);
            return null;
          }
        };

        const updateMatchesWithOdds = async (matches) => {
          const matchesWithOdds = await Promise.all(
            matches.map(async (match) => {
              const odds = await fetchMatchOdds(match.id);
              if (odds) match.odds = odds;
              return match;
            })
          );
          return matchesWithOdds.sort((a, b) => {
            if (a.inplay && !b.inplay) return -1;
            if (!a.inplay && b.inplay) return 1;
            return new Date(a.marketStartTime) - new Date(b.marketStartTime);
          });
        };

        const [allSportsWithOdds, cricketWithOdds, soccerWithOdds, tennisWithOdds] = await Promise.all([
          updateMatchesWithOdds(allSportsProcessed),
          updateMatchesWithOdds(cricketProcessed),
          updateMatchesWithOdds(soccerProcessed),
          updateMatchesWithOdds(tennisProcessed)
        ]);

        setAllSportsMatches(allSportsWithOdds);
        setCricketMatches(cricketWithOdds);
        setSoccerMatches(soccerWithOdds);
        setTennisMatches(tennisWithOdds);

        if (sporttab === "all-sports") setMobileMatches(allSportsWithOdds);
        else if (sporttab === "cricket") setMobileMatches(cricketWithOdds);
        else if (sporttab === "soccer") setMobileMatches(soccerWithOdds);
        else if (sporttab === "tennis") setMobileMatches(tennisWithOdds);
      } catch (error) {
        console.error('Error fetching matches:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllMatches();
    const interval = setInterval(fetchAllMatches, 30000);
    return () => clearInterval(interval);
  }, [sporttab]);

  useEffect(() => {
    if (sporttab === "all-sports") setMobileMatches(allSportsMatches);
    else if (sporttab === "cricket") setMobileMatches(cricketMatches);
    else if (sporttab === "soccer") setMobileMatches(soccerMatches);
    else if (sporttab === "tennis") setMobileMatches(tennisMatches);
  }, [sporttab, allSportsMatches, cricketMatches, soccerMatches, tennisMatches]);

  // --- Handlers ---
  const handleSportTabClick = (tabValue) => {
    setSportTab(tabValue);
    history.push(`/sports/markets/in-play`);
  };

  // const handleMatchClick = (matchId) => {
  //   if (matchId) history.push(`/sports/markets/${matchId}`);
    
  // };

  
  const handleMatchClick = (matchId) => {
    // Find the match data from matches array
    const selectedMatch = mobileMatches?.find(match => match.id === matchId);

    // Push to history with match data in state
    history.push({
      pathname: `/sports/markets/${matchId}`,
      state: {
        matchData: {
          ...selectedMatch,
          matchId: matchId,
          eventId: selectedMatch?.eventid,
          matchTitle: `${selectedMatch.team1} v ${selectedMatch.team2}`,
          competition: selectedMatch.competition,
          status: selectedMatch.status,
          isLive: selectedMatch.isLive,
          isSuspended: selectedMatch.isSuspended
        }
      }
    });
  };

  const handleDotClick = (index) => {
    // index is 0..totalSlides-1; map to real leftmost index by offset with viewportCards
    setCurrentSlide(index + viewportCards);
  };

  // Utility for active dot index
  const activeDotIndex = ((currentSlide - viewportCards) % totalSlides + totalSlides) % totalSlides;

  // --- Render ---
  return (
    <>
      <div
        ref={containerRef}
        style={{
          width: `${CARD_WIDTH * viewportCards + CARD_GAP * (viewportCards - 1)}px`,
          margin: '0 auto',
          paddingTop: '10px',
          overflow: 'hidden',
          height: '12.75rem',
          position: 'relative',
        }}
        className="tabcard-scroll-container"
      >
        <div
          style={{
            display: 'flex',
            gap: `${CARD_GAP}px`,
            transform: `translateX(-${currentSlide * (CARD_WIDTH + CARD_GAP)}px)`,
            transition:
              containerRef.current?.style.transition === 'none'
                ? 'none'
                : 'transform 0.5s ease',
            width: `${(CARD_WIDTH + CARD_GAP) * extendedSportsData.length}px`,
          }}
        >
          {extendedSportsData.map((sport, index) => {
            // pick first match per sport tab
            let firstMatch = null;
            if (sport.tab === 'all-sports' && allSportsMatches.length > 0) firstMatch = allSportsMatches[0];
            else if (sport.tab === 'cricket' && cricketMatches.length > 0) firstMatch = cricketMatches[0];
            else if (sport.tab === 'soccer' && soccerMatches.length > 0) firstMatch = soccerMatches[0];
            else if (sport.tab === 'tennis' && tennisMatches.length > 0) firstMatch = tennisMatches[0];

            const team1 = firstMatch?.team1 || sport.name;
            const team2 = firstMatch?.team2 || 'Matches';
            const statusText = firstMatch ? firstMatch.status : (isLoading ? 'Loading matches...' : 'No matches');

            return (
              <TabCard
                key={`${sport.id}-${index}`}
                onClick={() => handleSportTabClick(sport.tab)}
                sport={sport.tab}
                style={{ width: `${CARD_WIDTH}px` }}
              >
                <TabCardContent>
                  <TabCardBadge>{sport.name}</TabCardBadge>
                <TabCardTitle>{team1}</TabCardTitle>
                    <TabCardTitle>VS</TabCardTitle>
                 <TabCardTitle> {team2}</TabCardTitle>
                  <TabCardDesc>{statusText}</TabCardDesc>
                  <PlayNowButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMatchClick(firstMatch?.id);
                    }}
                    disabled={!firstMatch}
                    title={firstMatch ? 'Open market' : 'No market available'}
                  >
                    Play Now
                  </PlayNowButton>
                </TabCardContent>
                <TabCardImage
                  src={sport.image || '/assets/images/sports/placeholder.png'}
                  alt={sport.name}
                />
              </TabCard>
            );
          })}
        </div>
      </div>

      {/* Navigation Dots (match only original slides) */}
      <DotsContainer>
        {sportsData.map((_, index) => (
          <Dot
            key={index}
            active={index === activeDotIndex}
            onClick={() => handleDotClick(index)}
          />
        ))}
      </DotsContainer>
    </>
  );
};

// ---------------- styled components ----------------

const DotsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 4px;
 
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => (props.active ? '#25ee89' : 'rgba(255, 255, 255, 0.3)')};
  cursor: pointer;
  transition: background-color 0.3s ease;
  &:hover {
    background-color: #25ee89;
  }
`;

const TabCard = styled.div`
  display: flex;
  align-items: center;
  background: ${(props) => {
    switch (props.sport) {
      case 'cricket': return 'linear-gradient(90deg, rgba(34, 197, 94, 0.3), rgba(22, 163, 74, 0.9))';
      case 'soccer': return 'linear-gradient(90deg, rgba(59, 130, 246, 0.3), rgba(37, 99, 235, 0.9))';
      case 'tennis': return 'linear-gradient(90deg, rgba(251, 146, 60, 0.3), rgba(239, 68, 68, 0.9))';
      default: return 'linear-gradient(90deg, rgba(240, 156, 6, 0.3), rgba(30, 35, 40, 0.9))';
    }
  }};
  border-radius: 16px;
  padding: 0.2rem 0.2rem 0.2rem 1rem;
  margin-bottom: 18px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  flex-shrink: 0;
  cursor: pointer;
  transition: box-shadow 0.2s;
  font-family: 'Montserrat Alternates', sans-serif;
  width: 390px;
  overflow: hidden;
  &:hover {
    box-shadow: 0 4px 16px rgba(240,156,6,0.15);
  }
`;

const TabCardImage = styled.img`
  width: 11rem;
  height: 11rem;
  border-radius: 18px;
  object-fit: cover;
  margin-left: 18px;
  box-shadow: 0 2px 12px rgba(34,49,58,0.18);
  background: transparent;
  flex-shrink: 0;
`;

const TabCardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: calc(100% - 11rem - 18px);
  padding-right: 8px;
`;

const TabCardBadge = styled.div`
  background: linear-gradient(45deg, #f7dd00f5, transparent);
  color: #22313a;
  font-size: 12px;
  font-weight: 700;
  border-radius: 6px;
  padding: 2px 10px;
  display: inline-block;
  margin-bottom: 8px;
`;

const TabCardTitle = styled.div`
  display: block;
  font-size: 0.8rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 4px;
  font-family: 'Montserrat Alternates', sans-serif;

  /* key parts */
  flex: 1;              /* allow it to shrink inside flexbox */
  min-width: 0;         /* CRUCIAL: otherwise ellipsis never triggers */
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;



const TabCardDesc = styled.div`
  font-size: 13px;
  color: #bfc9d1;
 
`;

const PlayNowButton = styled.button`
  background: linear-gradient(45deg, #25ee89, #ff6b35);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 8px;
  font-family: 'Montserrat Alternates', sans-serif;
  transition: all 0.3s ease;
  opacity: ${(p) => (p.disabled ? 0.6 : 1)};
  pointer-events: ${(p) => (p.disabled ? 'none' : 'auto')};
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(240, 156, 6, 0.3);
  }
`;

export default SportsBanner;

// Hide scrollbars for the container
const style = document.createElement('style');
style.textContent = `
  .tabcard-scroll-container::-webkit-scrollbar {
    display: none;
  }
  .tabcard-scroll-container {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;
document.head.appendChild(style);
